import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type PorscheMasterDataModuleSpecsFragment } from '../../../../api/fragments/PorscheMasterDataModuleSpecs';
import {
    UpdatePorscheMasterDataModuleDocument,
    type UpdatePorscheMasterDataModuleMutation,
    type UpdatePorscheMasterDataModuleMutationVariables,
} from '../../../../api/mutations/updatePorscheMasterDataModule';
import { type PorscheMasterDataModuleSettings } from '../../../../api/types';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import PorscheMasterDataModuleUpdateForm from './PorscheMasterDataModuleUpdateForm';

type PorscheMasterDataModuleDetailsInnerProps = {
    module: PorscheMasterDataModuleSpecsFragment;
};

const validator = validators.compose(
    validators.requiredString('displayName'),
    validators.requiredString('ggId'),
    validators.requiredString('ppnUrl'),
    validators.requiredString('ppnClientId'),
    validators.requiredString('ppnClientSecret'),
    validators.requiredString('ppnResource'),
    validators.requiredString('pccdUrlPrefix'),
    validators.requiredString('pccdClientId'),
    validators.requiredString('pccdClientSecret'),
    validators.requiredString('pccdToken')
);

const PorscheMasterDataModuleDetailsInner = ({ module }: PorscheMasterDataModuleDetailsInnerProps) => {
    const { t } = useTranslation(['porscheMasterDataModuleDetails', 'moduleDetails']);

    const apolloClient = useApolloClient();

    const secrets = module.setting?.secrets;
    const initialValues = useMemo<PorscheMasterDataModuleSettings>(
        () => ({
            displayName: module.displayName,
            ggId: module.setting?.ggId,
            ...pick(
                [
                    'ppnUrl',
                    'ppnClientId',
                    'ppnClientSecret',
                    'ppnResource',
                    'pccdUrlPrefix',
                    'pccdClientId',
                    'pccdClientSecret',
                    'pccdToken',
                ],
                secrets
            ),
        }),
        [secrets, module.displayName, module.setting?.ggId]
    );

    const onSubmit = useHandleError(
        async (values: PorscheMasterDataModuleSettings) => {
            if (!values.displayName) {
                return;
            }

            message.loading({
                content: t('porscheMasterDataModuleDetails:messages.updateMainDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdatePorscheMasterDataModuleMutation, UpdatePorscheMasterDataModuleMutationVariables>({
                    mutation: UpdatePorscheMasterDataModuleDocument,
                    variables: { moduleId: module.id, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('porscheMasterDataModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });
        },
        [module.id, apolloClient, t]
    );

    const validate = useValidator(validator);

    return (
        <Formik<PorscheMasterDataModuleSettings> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updatePorscheMasterDataModule"
                    layout="vertical"
                    name="updatePorscheMasterDataModule"
                    onSubmitCapture={handleSubmit}
                >
                    <PorscheMasterDataModuleUpdateForm module={module} />
                </Form>
            )}
        </Formik>
    );
};

export default PorscheMasterDataModuleDetailsInner;
